import React from 'react'
import { useQuery } from '@apollo/react-hooks'
import gql from 'graphql-tag'

import { i18n } from '@thg-commerce/enterprise-core'
import { EnterpriseLoyalty } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Account/LoyaltyRewards'
import { Currency } from '@thg-commerce/enterprise-network/src/generated/graphql'
import { ProgressBar } from '@thg-commerce/gravity-elements'
import { TextStyle } from '@thg-commerce/gravity-theme'

import {
  BottomContainer,
  Container,
  ProgressBarContainer,
  StatusInfo,
  StatusTitle,
  StyledStatusButton,
  TopContainer,
} from './styles'

export interface ButtonStateStyleInterface {
  button: {
    backgroundColor: string
    border: {
      width: string
      color: string
    }
    text: {
      style: TextStyle
      color: string
      textDecoration: string
    }
    boxShadow: {
      shadowX: string
      shadowY: string
      shadowBlur: string
      shadowSpread: string
      shadowColor: string
    }
  }
}

export interface LoyaltyStatusStyleInterface {
  default: ButtonStateStyleInterface
  hover: ButtonStateStyleInterface
  focus: ButtonStateStyleInterface
  active: ButtonStateStyleInterface
}

interface LoyaltyBarProps {
  originUrl: string
  loyaltyEnabled?: boolean
  currency: string
}

interface TierProps {
  name: string
  order: number
  threshold: number
}

interface LoyaltyStatusResponse {
  currentPoints: number
  pointsToNextTier: number
  nextThresholdRequiredPoints: number
  tierNames: string[]
  topTier: boolean
}

const getCurrentLoyaltyTierNames = (
  tiers: TierProps[] | null | undefined,
  currentPoints: number,
): string[] => {
  if (!tiers?.length || currentPoints == null) {
    return []
  }

  const foundTier = tiers.find((tier: TierProps, i: number): boolean => {
    if (i === tiers.length - 1) {
      return true
    }
    return (
      currentPoints >= tier.threshold && currentPoints < tiers[i + 1].threshold
    )
  })

  const tierIndex = tiers.indexOf(foundTier as TierProps)

  if (tierIndex === tiers.length - 1) {
    return [tiers[tierIndex]['name']]
  }
  if (foundTier) {
    return [tiers[tierIndex]['name'], tiers[tierIndex + 1]['name']]
  }
  return []
}

const LOYALTY_WIDGET = gql`
  query Loyalty(
    $currency: Currency!
    $enableLoyaltyAccountDashboard: Boolean! = false
  ) {
    loyaltyRewards(
      currency: $currency
      enableLoyaltyAccountDashboard: $enableLoyaltyAccountDashboard
    ) @client {
      firstName
      summary {
        tierOrder
        totalPoints
        pointsToNextTier
      }
      tiers {
        name
        order
        threshold
      }
    }
  }
`

const useLoyaltyStatus = (
  currency: Currency,
  loyaltyEnabled?: boolean,
): LoyaltyStatusResponse | null => {
  const { data } = useQuery<EnterpriseLoyalty>(LOYALTY_WIDGET, {
    variables: {
      currency,
      enableLoyaltyAccountDashboard: loyaltyEnabled || false,
    },
    fetchPolicy: 'no-cache',
    ssr: typeof window !== 'undefined',
  })

  if (!data) return null

  const pointsToNextTier =
    data.loyaltyRewards.loyaltyAccountDashboard.summary.pointsToNextTier
  const currentPoints =
    data.loyaltyRewards.loyaltyAccountDashboard.summary.totalPoints
  const tiers = data.loyaltyRewards.loyaltyAccountDashboard.tiers

  if (pointsToNextTier == null || currentPoints == null || !tiers) {
    return null
  }

  const tierNames = getCurrentLoyaltyTierNames(tiers, currentPoints)

  const nextThresholdRequiredPoints =
    tierNames.length === 1
      ? tiers[tiers.length - 1].threshold
      : pointsToNextTier + currentPoints

  const topTier = tierNames.length === 1

  return {
    currentPoints,
    pointsToNextTier,
    nextThresholdRequiredPoints,
    tierNames,
    topTier,
  }
}

const handleOnClickNavigate = (url: string) => {
  window.location.assign(url)
}

const createProgressSteps = (totalPoints: number) => {
  return Array.from({ length: totalPoints }, (_, index) => ({
    label: `${index + 1}`,
  }))
}

export const LoyaltyStatusBar = (props: LoyaltyBarProps) => {
  const i18nText = {
    loyaltyPointsStatusBar: {
      title: {
        text: i18n('loyaltypointsstatusbar.title.text'),
      },
      statLabel: {
        text: i18n('loyaltypointsstatusbar.statLabel.text'),
      },
      statusMessage: {
        part1: {
          text: i18n('loyaltypointsstatusbar.statusmessage.part1.text'),
        },
        part2: {
          text: i18n('loyaltypointsstatusbar.statusmessage.part2.text'),
        },
        part3: {
          text: i18n('loyaltypointsstatusbar.statusmessage.part3.text'),
        },
        part4: {
          text: i18n('loyaltypointsstatusbar.statusmessage.part4.text'),
        },
        part5: {
          text: i18n('loyaltypointsstatusbar.statusmessage.part5.text'),
        },
        part6: {
          text: i18n('loyaltypointsstatusbar.statusmessage.part6.text'),
        },
      },
      button: {
        link: i18n('account.loyalty.link'),
        text: i18n('loyaltypointsstatusbar.button.text'),
      },
    },
  }

  const rewardsLink =
    props.originUrl + i18nText.loyaltyPointsStatusBar.button.link

  const loyaltyInfo = useLoyaltyStatus(
    props.currency as Currency,
    props.loyaltyEnabled,
  )

  if (!loyaltyInfo) {
    return null
  }
  const steps = createProgressSteps(loyaltyInfo.nextThresholdRequiredPoints)

  return (
    <Container>
      <TopContainer>
        <StatusTitle>{i18nText.loyaltyPointsStatusBar.title.text}</StatusTitle>
        <StatusTitle>{loyaltyInfo.currentPoints}</StatusTitle>
        <StatusInfo
          content={`
          ${i18nText.loyaltyPointsStatusBar.statLabel.text}
        `}
        />
      </TopContainer>
      <ProgressBarContainer>
        {loyaltyInfo?.topTier ? (
          <ProgressBar
            progressHeight="16px"
            enableAlternateProgressBar={true}
            steps={steps}
            activeStep={loyaltyInfo.currentPoints}
            totalSteps={loyaltyInfo.currentPoints}
            setGoalLabels={true}
            isNotRounded={true}
          />
        ) : (
          <ProgressBar
            progressHeight="16px"
            enableAlternateProgressBar={true}
            steps={steps}
            activeStep={loyaltyInfo.currentPoints}
            totalSteps={loyaltyInfo.nextThresholdRequiredPoints}
            setGoalLabels={true}
            isNotRounded={true}
          />
        )}
      </ProgressBarContainer>
      <BottomContainer>
        {loyaltyInfo?.topTier ? (
          <StatusInfo
            content={`
      ${i18nText.loyaltyPointsStatusBar.statusMessage.part1.text}
      ${loyaltyInfo.tierNames[0]}
      ${i18nText.loyaltyPointsStatusBar.statusMessage.part6.text}
      `}
          />
        ) : (
          <StatusInfo
            content={`
          ${i18nText.loyaltyPointsStatusBar.statusMessage.part1.text}
          ${loyaltyInfo.tierNames[0]}
          ${i18nText.loyaltyPointsStatusBar.statusMessage.part2.text}
          ${loyaltyInfo.pointsToNextTier}
          ${i18nText.loyaltyPointsStatusBar.statusMessage.part3.text}
          ${loyaltyInfo.tierNames[1]}
          ${i18nText.loyaltyPointsStatusBar.statusMessage.part4.text}
          `}
          />
        )}
        <StyledStatusButton onClick={() => handleOnClickNavigate(rewardsLink)}>
          {i18nText.loyaltyPointsStatusBar.button.text}
        </StyledStatusButton>
      </BottomContainer>
    </Container>
  )
}

export default LoyaltyStatusBar
